/**
 * Certificate gallery, assembled automatically from the files on disk.
 *
 * HOW IT WORKS
 *  - `scripts/generate-certificates-manifest.js` scans public/certificates/ and
 *    writes certificates.manifest.json (one { file, type } per file). It runs
 *    before every `npm start` / `npm run build`.
 *  - The META map below layers human-readable details onto a file by filename.
 *  - Anything in the folder without a META entry still appears, with a title
 *    guessed from its filename, so a new drop-in is visible immediately and
 *    you can fill in its details here whenever.
 *
 * TO ADD A CERTIFICATE
 *  1. Drop the PDF / PNG / JPG into public/certificates/ (a descriptive,
 *     kebab-case filename becomes a sensible fallback title on its own).
 *  2. Optionally add a META entry below for a polished title / issuer / date,
 *     and `verifyUrl` + `credlyId` if it has a Credly digital badge.
 *  3. For a PDF, run `node scripts/generate-certificate-thumbnails.js` to
 *     rasterize its first page into public/certificates/thumbnails/ as a
 *     static PNG, so the gallery shows an image instead of rendering the PDF
 *     in the browser. Until that's run, the page falls back to a live
 *     PdfPageCanvas render, so a freshly dropped PDF still works right away.
 *  4. Restart the dev server (the manifest regenerates on start).
 */
import manifest from "./certificates.manifest.json";

// Details keyed by filename in public/certificates/. Everything is optional;
// `order` pins an item near the top of the gallery (lower = earlier).
const META = {
  "google-ux-design-professional-certificate.pdf": {
    title: "Google UX Design Professional Certificate (v.3)",
    issuer: "Google · Coursera",
    date: "Aug 2026",
    verifyUrl: "https://www.credly.com/go/eCU9L7dy",
    credlyId: "86eee779-6977-48b1-a3d1-3ae7f0b1848d",
    order: 0,
  },

  // Baobab · Arizona State University
  "baobab-asu-basic-python-workshop.png": {
    title: "Basic Python Workshop",
    issuer: "Baobab · Arizona State University",
    date: "22 Aug 2026",
    order: 10,
  },
  "baobab-asu-intermediate-python-for-data-analysis.png": {
    title: "Intermediate Python for Data Analysis",
    issuer: "Baobab · Arizona State University",
    date: "22 Aug 2026",
    order: 3,
  },
  "baobab-asu-fundamentals-of-business-analysis.png": {
    title: "Fundamentals of Business Analysis (BA)",
    issuer: "Baobab · Arizona State University",
    date: "27 Aug 2026",
    order: 14,
  },
  "baobab-asu-achiever-capabilities-soft-skills.png": {
    title: "Achiever Capabilities: Employable Soft Skills",
    issuer: "Baobab · Arizona State University",
    date: "27 Aug 2026",
    order: 7,
  },

  // Baobab · Mastercard Foundation
  "baobab-mcf-scholars-program-onboarding.png": {
    title: "Mastercard Foundation Scholars Program Onboarding",
    issuer: "Baobab · Mastercard Foundation",
    date: "25 Aug 2026",
    order: 2,
  },
  "baobab-mcf-how-to-give-back-right-now.png": {
    title: "How To Give Back Right Now",
    issuer: "Baobab · Mastercard Foundation",
    date: "25 Aug 2026",
    order: 15,
  },
  "baobab-mcf-what-is-transformative-leadership.png": {
    title: "What is Transformative Leadership?",
    issuer: "Baobab · Mastercard Foundation",
    date: "25 Aug 2026",
    order: 9,
  },
  "baobab-mcf-5-common-leadership-myths.png": {
    title: "5 Common Leadership Myths",
    issuer: "Baobab · Mastercard Foundation",
    date: "25 Aug 2026",
    order: 5,
  },
  "baobab-mcf-inclusiveness-transformative-leadership-mindset.png": {
    title: "Inclusiveness: An Important Transformative Leadership Mindset",
    issuer: "Baobab · Mastercard Foundation",
    date: "25 Aug 2026",
    order: 12,
  },

  // Baobab
  "baobab-situational-adaptability-flexible-leadership.png": {
    title: "Situational Adaptability: Flexible Leadership",
    issuer: "Baobab",
    date: "25 Aug 2026",
    order: 16,
  },
  "baobab-effective-workplace-communication.png": {
    title: "Effective Workplace Communication",
    issuer: "Baobab",
    date: "27 Aug 2026",
    order: 11,
  },
  "baobab-smart-goals.png": {
    title: "SMART Goals",
    issuer: "Baobab",
    date: "27 Aug 2026",
    order: 1,
  },
  "baobab-interview-skills.png": {
    title: "Interview Skills",
    issuer: "Baobab",
    date: "27 Aug 2026",
    order: 4,
  },
  "baobab-how-to-write-a-cv-resume.png": {
    title: "How to Write a CV/Resume",
    issuer: "Baobab",
    date: "25 Aug 2026",
    order: 13,
  },
  "baobab-academic-integrity.png": {
    title: "Academic Integrity",
    issuer: "Baobab",
    date: "25 Aug 2026",
    order: 8,
  },
  "baobab-storytelling-in-social-media.png": {
    title: "How to Use Storytelling in Social Media",
    issuer: "Baobab",
    date: "22 Aug 2026",
    order: 6,
  },

  // Google · UX Design Professional Certificate (individual courses)
  "google-foundations-of-ux-design.pdf": {
    title: "Foundations of User Experience (UX) Design",
    issuer: "Google · Coursera",
    date: "23 Aug 2026",
  },
  "google-start-ux-design-process-empathize-define-ideate.pdf": {
    title: "Start the UX Design Process: Empathize, Define, and Ideate",
    issuer: "Google · Coursera",
    date: "23 Aug 2026",
  },
  "google-build-wireframes-low-fidelity-prototypes.pdf": {
    title: "Build Wireframes and Low-Fidelity Prototypes",
    issuer: "Google · Coursera",
    date: "23 Aug 2026",
  },
  "google-conduct-ux-research-test-early-concepts.pdf": {
    title: "Conduct UX Research and Test Early Concepts",
    issuer: "Google · Coursera",
    date: "23 Aug 2026",
  },
  "google-figma-high-fidelity-designs-prototypes.pdf": {
    title: "Create High-Fidelity Designs and Prototypes in Figma",
    issuer: "Google · Coursera",
    date: "23 Aug 2026",
  },
  "google-build-dynamic-user-interfaces-for-websites.pdf": {
    title: "Build Dynamic User Interfaces (UI) for Websites",
    issuer: "Google · Coursera",
    date: "23 Aug 2026",
  },
  "google-ux-for-social-good-prepare-for-jobs.pdf": {
    title: "Design a User Experience for Social Good & Prepare for Jobs",
    issuer: "Google · Coursera",
    date: "23 Aug 2026",
  },
  "google-accelerate-job-search-with-ai.pdf": {
    title: "Accelerate Your Job Search with AI",
    issuer: "Google · Coursera",
    date: "23 Aug 2026",
  },
  "google-foundations-data-data-everywhere.pdf": {
    title: "Foundations: Data, Data, Everywhere",
    issuer: "Google · Coursera",
    date: "28 Aug 2026",
  },
  "google-foundations-of-cybersecurity.pdf": {
    title: "Foundations of Cybersecurity",
    issuer: "Google · Coursera",
    date: "3 Sep 2026",
  },

  // Arizona State University · Professional Skills (CareerCatalyst)
  "asu-professional-skills-decision-making.pdf": {
    title: "Professional Skills: Decision-Making",
    issuer: "Arizona State University · CareerCatalyst",
    date: "28 Aug 2026",
  },
  "asu-professional-skills-futures-thinking.pdf": {
    title: "Professional Skills: Futures Thinking",
    issuer: "Arizona State University · CareerCatalyst",
    date: "23 Aug 2026",
  },
  "asu-professional-skills-information-gathering-and-vetting.pdf": {
    title: "Professional Skills: Information Gathering and Vetting",
    issuer: "Arizona State University · CareerCatalyst",
    date: "23 Aug 2026",
  },
  "asu-professional-skills-leading-through-effective-communication.pdf": {
    title: "Professional Skills: Leading Through Effective Communication",
    issuer: "Arizona State University · CareerCatalyst",
    date: "12 Sep 2026",
  },
  "asu-professional-skills-public-speaking.pdf": {
    title: "Professional Skills: Public Speaking",
    issuer: "Arizona State University · CareerCatalyst",
    date: "12 Sep 2026",
  },
  "asu-professional-skills-managing-up.pdf": {
    title: "Professional Skills: Managing Up",
    issuer: "Arizona State University · CareerCatalyst",
    date: "12 Sep 2026",
  },
  "asu-professional-skills-team-communication.pdf": {
    title: "Professional Skills: Team Communication",
    issuer: "Arizona State University · CareerCatalyst",
    date: "12 Sep 2026",
  },
  "asu-professional-skills-communicating-your-best-self.pdf": {
    title: "Professional Skills: Communicating Your Best Self",
    issuer: "Arizona State University · CareerCatalyst",
    date: "12 Sep 2026",
  },
  "asu-professional-skills-giving-feedback.pdf": {
    title: "Professional Skills: Giving Feedback",
    issuer: "Arizona State University · CareerCatalyst",
    date: "12 Sep 2026",
  },

  // Arizona State University · Human Factors & Usability Engineering (Human Systems Engineering)
  "asu-what-is-the-human-factor-in-design.pdf": {
    title: "What is the Human Factor in Design?",
    issuer: "Arizona State University",
    date: "28 Aug 2026",
  },
  "asu-the-limits-of-human-perception.pdf": {
    title: "The Limits of Human Perception",
    issuer: "Arizona State University",
    date: "28 Aug 2026",
  },
  "asu-anthropometry-biomechanics-motor-skills-in-user-design.pdf": {
    title: "Anthropometry, Biomechanics, and Motor Skills in User Design",
    issuer: "Arizona State University",
    date: "28 Aug 2026",
  },
  "asu-human-cognitive-capacities-and-limitations.pdf": {
    title: "Human Cognitive Capacities & Limitations",
    issuer: "Arizona State University",
    date: "28 Aug 2026",
  },
  "asu-how-can-we-measure-and-study-human-error.pdf": {
    title: "How Can We Measure and Study Human Error?",
    issuer: "Arizona State University",
    date: "28 Aug 2026",
  },

  // Arizona State University · Technology & Entrepreneurship
  "asu-curiosity-necessary-ingredient-for-innovation.pdf": {
    title: "Curiosity: A Necessary Ingredient for Successful Innovation",
    issuer: "Arizona State University",
    date: "23 Aug 2026",
  },
  "asu-problem-solve-like-an-entrepreneur.pdf": {
    title: "Putting it All Together: Problem-Solve Like an Entrepreneur",
    issuer: "Arizona State University",
    date: "23 Aug 2026",
  },
  "asu-what-is-technology-entrepreneurship-and-innovation.pdf": {
    title: "What is Technology Entrepreneurship and Innovation?",
    issuer: "Arizona State University",
    date: "23 Aug 2026",
  },
  "asu-care-first-step-in-tech-innovation-for-entrepreneurs.pdf": {
    title: "Care: The First Step in Tech Innovation for Entrepreneurs",
    issuer: "Arizona State University",
    date: "23 Aug 2026",
  },

  // Arizona State University · ASU-West (Digital Safety and Security specialization)
  "asu-digital-creativity.pdf": {
    title: "Digital Creativity",
    issuer: "Arizona State University",
    date: "13 Sep 2026",
  },
  "asu-digital-safety-and-security.pdf": {
    title: "Digital Safety and Security",
    issuer: "Arizona State University",
    date: "13 Sep 2026",
  },
  "asu-digital-communication-and-emotion.pdf": {
    title: "Digital Communication and Emotion",
    issuer: "Arizona State University",
    date: "13 Sep 2026",
  },

  // Arizona State University · other individual courses
  "asu-lesson-planning-with-the-ell-in-mind.pdf": {
    title: "Lesson Planning with the ELL in Mind",
    issuer: "Arizona State University · Global Launch",
    date: "31 Aug 2026",
  },
  "asu-decision-making.pdf": {
    title: "Decision-Making",
    issuer: "Arizona State University",
    date: "13 Sep 2026",
  },

  // University of Alberta
  "alberta-client-needs-and-software-requirements.pdf": {
    title: "Client Needs and Software Requirements",
    issuer: "University of Alberta · Coursera",
    date: "13 Sep 2026",
  },
};

const KNOWN_ACRONYMS = { asu: "ASU", ba: "BA", cv: "CV", smart: "SMART", ux: "UX", ui: "UI" };
const DROP_PREFIX = /^(baobab|asu|mcf)-/;

/** Turn "baobab-asu-basic-python-workshop.png" into "Basic Python Workshop". */
function titleFromFilename(file) {
  let slug = file.replace(/\.[^.]+$/, "");
  while (DROP_PREFIX.test(slug)) slug = slug.replace(DROP_PREFIX, "");
  return slug
    .split("-")
    .map((w) => KNOWN_ACRONYMS[w] || w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function idFromFilename(file) {
  return file.replace(/\.[^.]+$/, "");
}

const certificates = manifest
  .map(({ file, type, hasThumbnail }) => {
    const meta = META[file] || {};
    const id = idFromFilename(file);
    return {
      id,
      file,
      type,
      // Pre-rendered first-page PNG (see scripts/generate-certificate-thumbnails.js),
      // used instead of rendering the PDF client-side. Falls back to a live
      // PdfPageCanvas render if a PDF hasn't had its thumbnail generated yet.
      thumbnail: type === "pdf" && hasThumbnail ? `${id}.png` : null,
      title: meta.title || titleFromFilename(file),
      issuer: meta.issuer || "Baobab",
      date: meta.date || "",
      verifyUrl: meta.verifyUrl,
      credlyId: meta.credlyId,
      order: meta.order ?? Number.MAX_SAFE_INTEGER,
    };
  })
  .sort((a, b) => {
    if (a.order !== b.order) return a.order - b.order;
    const da = a.date ? Date.parse(a.date) : 0;
    const db = b.date ? Date.parse(b.date) : 0;
    if (da !== db) return db - da; // newest first
    return a.title.localeCompare(b.title);
  });

export default certificates;
